import {
  collection,
  addDoc,
  getDocs,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { DRXError } from '@/lib/drx-error';
import type {
  ITestimonialRepository,
  CreateTestimonialData,
  GetTestimonialData,
} from './interfaces/testimonial.repository.interface';

export class TestimonialRepository implements ITestimonialRepository {
  private collectionName = 'testimonials';

  async getTestimonials(): Promise<GetTestimonialData[]> {
    try {
      const colRef = collection(db, this.collectionName);
      const snapshot = await getDocs(colRef);
      const testimonials = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as unknown as GetTestimonialData[];

      // Sort by creation date (newest first)
      return testimonials.sort((a: any, b: any) => {
        const aDate = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
        const bDate = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
        return bDate.getTime() - aDate.getTime();
      });
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'TESTIMONIAL_GET_ALL_FAILED',
        devMessage: 'Failed to get testimonials',
        cause: error,
        level: 'error',
      });
    }
  }

  async createTestimonial(testimonialData: CreateTestimonialData): Promise<GetTestimonialData> {
    try {
      const colRef = collection(db, this.collectionName);
      const docRef = await addDoc(colRef, {
        ...testimonialData,
        createdAt: serverTimestamp(),
        isApproved: false,
      });

      return {
        id: docRef.id,
        ...testimonialData,
        isApproved: false,
        createdAt: new Date(),
      } as GetTestimonialData;
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'TESTIMONIAL_CREATE_FAILED',
        devMessage: `Failed to create testimonial for user ${testimonialData.userId}`,
        cause: error,
        level: 'error',
      });
    }
  }
}
